<script lang="ts">
import {defineComponent, PropType} from 'vue'
import GlobalResponsiveImage from "@global/global-elements/global-responsive-image/global-responsive-image.vue";
import {DownloadLicenseEnum, MediaGalleryAssetType} from "../types/MediaGalleryAssetType";
import GlobalInputCheckbox from "@global/global-elements/global-input-checkbox/global-input-checkbox.vue";
import GlobalButton from "@global/global-elements/global-button/global-button.vue";
import {mapGetters} from "vuex";

export default defineComponent({
    name: "MediaGalleryAssetModal",
    components: {GlobalButton, GlobalInputCheckbox, GlobalResponsiveImage},
    props: {
        asset: {
            type: Object as PropType<MediaGalleryAssetType>,
            required: true
        }
    },
    computed: {
        DownloadLicenseEnum() {
            return DownloadLicenseEnum
        },
        ...mapGetters({
            hasRunningRequest: 'hasRunningRequest'
        }),
        isSelected() {
            return this.$store.getters.isAssetSelected(this.asset.id)
        },
        removeRequestRunning() {
            return this.hasRunningRequest('collectionRemove' + this.asset.id)
        },
        addRequestRunning() {
            return this.hasRunningRequest('collectionAdd' + this.asset.id)
        }
    },
    methods: {
        downloadAsset() {
            this.$store.dispatch('downloadAsset', this.asset)
        }
    }
})
</script>

<template>
    <div class="media-gallery-asset-modal">
        <div class="media-gallery-asset-modal__headline">
            <span class="lead">
            {{asset.headline}}
            </span>
            <template v-if="asset.subline">
                <br>
                {{asset.subline}}
            </template>
        </div>
        <div class="media-gallery-asset-modal__image">
            <img v-if="asset.image" :src="asset.image" :alt="asset.description ? asset.description : asset.headline" :title="asset.description ? asset.description : ''" class="img-fluid img"/>
            <img v-else-if="asset.thumbnail" :src="asset.thumbnail" :alt="asset.description ? asset.description : asset.headline" class="img-fluid thumb"/>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160" class="img-fluid">
                <g fill="none" fill-rule="evenodd">
                    <path fill="#CCC" fill-rule="nonzero" d="M93 29H39v103h81V56z"/>
                    <path fill="#FFF" fill-rule="nonzero" d="M93 34v22h22v70H44V34z"/>
                    <path fill="#ABABAB" d="M71 94h59v22H71z"/>
                    <text fill="#000" font-size="15" font-weight="300" transform="translate(71 94)">
                        <tspan x="16" y="17">{{ asset.meta_type.toUpperCase() }}</tspan>
                    </text>
                </g>
            </svg>
        </div>
        <div class="media-gallery-asset-modal__information">
            <div class="media-gallery-asset-modal__text">
                <section class="media-gallery-asset-modal__section">
                    <header class="media-gallery-asset-modal__section-header">{{ $t('mediaGallery.modal.license.headline') }}</header>
                    <p v-if="asset.copyright">
                        {{ $t('mediaGallery.modal.license.copyright') }}: {{ asset.copyright }}
                    </p>
                    <p>
                        {{ asset.licenses?.includes(DownloadLicenseEnum.ONLINE) ? $t('mediaGallery.modal.license.online.yes') : $t('mediaGallery.modal.license.online.no') }}<br />
                        {{ asset.licenses?.includes(DownloadLicenseEnum.PRINT) ? $t('mediaGallery.modal.license.print.yes') : $t('mediaGallery.modal.license.print.no') }}<br />
                        {{ asset.licenses?.includes(DownloadLicenseEnum.SHARE_THIRD_PARTY) ? $t('mediaGallery.modal.license.share.yes') : $t('mediaGallery.modal.license.share.no') }}
                    </p>
                </section>
                <section class="media-gallery-asset-modal__section">
                    <header class="media-gallery-asset-modal__section-header">{{ $t('mediaGallery.modal.file_meta.headline') }}</header>
                    <p>
                        <span v-if="asset.meta_width && asset.meta_height">{{ $t('mediaGallery.modal.file_meta.dimension') }}: {{ asset.meta_width }} x {{ asset.meta_height }}<br /></span>
                        <span v-if="asset.meta_dpi">{{ $t('mediaGallery.modal.file_meta.resolution') }}: {{asset.meta_dpi}}<br /></span>
                        <span>{{ $t('mediaGallery.modal.file_meta.type') }}: {{ asset.meta_type.toLowerCase() }}<br /></span>
                        <span>{{ $t('mediaGallery.modal.file_meta.size') }}: {{ asset.meta_filesize }}<br /></span>
                        <span v-if="asset.meta_files">{{ $t('mediaGallery.modal.file_meta.content') }}: {{asset.meta_files}}<br /></span>
                    </p>
                </section>
            </div>

            <div class="media-gallery-asset-modal__actions">
                <global-input-checkbox :label="$t('mediaGallery.modal.actions.select')" :value="isSelected" @input="$store.dispatch('toggleAssetSelection', asset.id)"/>
                <global-button :icon="hasRunningRequest('download') ? 'spin' : 'download'" @click="downloadAsset" :disabled="hasRunningRequest('download')">{{
                        $t('mediaGallery.modal.actions.download')
                    }}
                </global-button>
                <global-button :icon="addRequestRunning ? 'spin' : 'collection-add'" theme="secondary" v-if="!asset.collected" :disabled="addRequestRunning" @click="$store.dispatch('toggleAssetCollection', asset)">
                    {{ $t('mediaGallery.modal.actions.addToCollection') }}
                </global-button>
                <global-button :icon="removeRequestRunning ? 'spin' : 'collection-remove'" theme="secondary" v-if="asset.collected" :disabled="removeRequestRunning" @click="$store.dispatch('toggleAssetCollection', asset)">
                    {{ $t('mediaGallery.modal.actions.removeFromCollection') }}
                </global-button>
                <slot name="actions"/>
            </div>
        </div>

        <div class="media-gallery-asset-modal__pagination">
            <slot name="pagination">
            </slot>
        </div>
    </div>
</template>
